/**
 * Shared (public) Government Intake API.
 * Backend: shared.routes.ts — POST /v0/shared/government_intakes (public create), assets/sign, system-pending.
 * Used by the public form at /government-intake when user clicks "Submit Discovery".
 */

import axios from 'axios';
import { HttpMethods } from '@/api/base';
import { BASE_PATH_SHARED } from './constants';

/** Backend publicCreateHandler expects { payload?, files?, status?, source?, archived? }. */
export interface CreatePublicIntakeRequest {
  payload: Record<string, unknown>;
  files?: Array<{ name: string; type: string; size: number; key?: string; path?: string; storageKey?: string; sectionPath?: string }>;
  status?: string;
  source?: string;
  archived?: boolean;
}

export interface CreatePublicIntakeResponse {
  data: { id: number; [key: string]: unknown };
}

/**
 * Backend: POST /v0/shared/government_intakes (shared.routes.ts publicCreateHandler).
 * Submits the form payload as JSON; used when user clicks "Submit Discovery" on the public form.
 */
export async function createPublicIntake(data: CreatePublicIntakeRequest): Promise<CreatePublicIntakeResponse> {
  const res = await axios.request<CreatePublicIntakeResponse>({
    method: HttpMethods.POST,
    url: BASE_PATH_SHARED,
    data,
  });
  return res.data;
}

/** Backend publicSignAssetHandler expects { fileName, fileType, fileSize, sectionPath? }. Returns signed upload URL. */
export interface SignAssetRequest {
  fileName: string;
  fileType: string;
  fileSize: number;
  sectionPath?: string;
}

export interface SignAssetResponse {
  url: string;
  path: string;
  key: string;
}

/**
 * Backend: POST /v0/shared/government_intakes/assets/sign (shared.routes.ts publicSignAssetHandler).
 * Returns a signed URL to upload the file
 */
export async function signAsset(data: SignAssetRequest): Promise<SignAssetResponse> {
  const res = await axios.request<SignAssetResponse>({
    method: HttpMethods.POST,
    url: `${BASE_PATH_SHARED}/assets/sign`,
    data,
  });
  return res.data;
}

/** Backend: POST /v0/shared/government_intakes/system-pending — submit a pending system suggestion. */
export interface CreateSystemPendingRequest {
  submittedName: string;
  submittedVendor?: string;
  submittedNotes?: string;
}

export interface CreateSystemPendingResponse {
  data: {
    id: number;
    submittedName: string;
    submittedVendor?: string | null;
    submittedNotes?: string | null;
    status: string;
  };
}

export async function createSystemPending(data: CreateSystemPendingRequest): Promise<CreateSystemPendingResponse> {
  const res = await axios.request<CreateSystemPendingResponse>({
    method: HttpMethods.POST,
    url: `${BASE_PATH_SHARED}/system-pending`,
    data,
  });
  return res.data;
}

export interface PublicSystemCatalogListItem {
  id: number;
  name: string;
  vendor: string | null;
  category: string | null;
  aliases: unknown;
  isActive: boolean;
}

export interface GetPublicSystemCatalogListResponse {
  data: PublicSystemCatalogListItem[];
}

/** Backend: GET /v0/shared/government_intakes/system-catalog — list active catalog options for public intake flows. */
export async function getPublicSystemCatalogList(params?: {
  search?: string;
  isActive?: boolean;
}): Promise<GetPublicSystemCatalogListResponse> {
  const res = await axios.request<GetPublicSystemCatalogListResponse>({
    method: HttpMethods.GET,
    url: `${BASE_PATH_SHARED}/system-catalog`,
    params: params ?? undefined,
  });
  return res.data;
}
